import React, { useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Camera, Image as ImageIcon, Loader2, Check, AlertTriangle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/phone-capture/public`;

const compressImage = (file) => new Promise((resolve) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
        URL.revokeObjectURL(url);
        const max = 1800;
        let { width, height } = img;
        if (width > max || height > max) {
            const r = Math.min(max / width, max / height);
            width = Math.round(width * r);
            height = Math.round(height * r);
        }
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        canvas.getContext('2d').drawImage(img, 0, 0, width, height);
        canvas.toBlob((blob) => resolve(blob || file), 'image/jpeg', 0.85);
    };
    img.onerror = () => { URL.revokeObjectURL(url); resolve(file); };
    img.src = url;
});

export default function PhoneCapturePage() {
    const { sessionId } = useParams();
    const [params] = useSearchParams();
    const token = params.get('t') || '';
    const [status, setStatus] = useState('loading'); // loading | invalid | ready | finished
    const [errMsg, setErrMsg] = useState('');
    const [multiple, setMultiple] = useState(true);
    const [photos, setPhotos] = useState([]);
    const [uploading, setUploading] = useState(false);
    const cameraRef = useRef(null);
    const galleryRef = useRef(null);

    useEffect(() => {
        fetch(`${API}/${sessionId}/meta?t=${token}`)
            .then(async (r) => {
                if (!r.ok) {
                    const d = await r.json().catch(() => ({}));
                    setErrMsg(d.detail || 'Lien invalide ou expiré');
                    setStatus('invalid');
                    return;
                }
                const d = await r.json();
                setMultiple(d.multiple !== false);
                setStatus(d.done ? 'finished' : 'ready');
            })
            .catch(() => { setErrMsg('Connexion impossible'); setStatus('invalid'); });
    }, [sessionId, token]);

    const markDone = async () => {
        try { await fetch(`${API}/${sessionId}/done?t=${token}`, { method: 'POST' }); } catch { /* ignore */ }
        setStatus('finished');
    };

    const upload = async (file) => {
        setUploading(true);
        try {
            const blob = await compressImage(file);
            const fd = new FormData();
            fd.append('file', blob, 'photo.jpg');
            const r = await fetch(`${API}/${sessionId}/photos?t=${token}`, { method: 'POST', body: fd });
            if (!r.ok) {
                const d = await r.json().catch(() => ({}));
                alert(d.detail || "Erreur d'envoi, réessayez");
                return;
            }
            setPhotos(prev => [...prev, URL.createObjectURL(blob)]);
            if (!multiple) await markDone();
        } catch {
            alert("Erreur d'envoi : vérifiez la connexion internet du téléphone");
        } finally {
            setUploading(false);
        }
    };

    const onPick = async (e) => {
        const files = Array.from(e.target.files || []);
        e.target.value = '';
        for (const f of files) await upload(f);
    };

    return (
        <div className="min-h-screen bg-slate-900 text-white flex flex-col" data-testid="phone-capture-page">
            <header className="px-5 pt-6 pb-3 text-center">
                <h1 className="text-xl font-bold tracking-wide !text-white">Sokuzai — Photo à distance</h1>
                <p className="text-slate-400 text-sm mt-1">Les photos partent directement sur le PC</p>
            </header>

            <main className="flex-1 flex flex-col items-center justify-center px-5 pb-8 gap-5">
                {status === 'loading' && <Loader2 className="w-10 h-10 animate-spin text-sky-400" />}

                {status === 'invalid' && (
                    <div className="text-center space-y-3" data-testid="capture-invalid">
                        <AlertTriangle className="w-12 h-12 text-amber-400 mx-auto" />
                        <p className="font-semibold">{errMsg}</p>
                        <p className="text-sm text-slate-400">Régénérez un QR code depuis le PC puis scannez à nouveau.</p>
                    </div>
                )}

                {status === 'finished' && (
                    <div className="text-center space-y-3" data-testid="capture-finished">
                        <div className="w-16 h-16 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto">
                            <Check className="w-9 h-9 text-emerald-400" />
                        </div>
                        <p className="font-semibold text-lg">C'est envoyé !</p>
                        <p className="text-sm text-slate-400">{photos.length > 0 ? `${photos.length} photo(s) transmise(s) au PC.` : 'Photos transmises au PC.'} Vous pouvez fermer cette page.</p>
                    </div>
                )}

                {status === 'ready' && (
                    <>
                        {photos.length > 0 && (
                            <div className="flex flex-wrap gap-2 justify-center" data-testid="capture-thumbs">
                                {photos.map((u, i) => (
                                    <div key={i} className="relative">
                                        <img src={u} alt={`Photo ${i + 1}`} className="h-24 w-20 object-cover rounded-lg border border-slate-700" />
                                        <span className="absolute bottom-0 inset-x-0 bg-black/60 text-[10px] text-center rounded-b-lg py-0.5">Envoyée ✓</span>
                                    </div>
                                ))}
                            </div>
                        )}

                        <input ref={cameraRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={onPick} />
                        <input ref={galleryRef} type="file" accept="image/*" multiple={multiple} className="hidden" onChange={onPick} />

                        <button
                            onClick={() => cameraRef.current?.click()}
                            disabled={uploading}
                            className="w-full max-w-xs bg-orange-500 active:bg-orange-600 disabled:opacity-60 rounded-2xl py-5 flex items-center justify-center gap-3 text-lg font-bold shadow-lg"
                            data-testid="capture-take-photo-btn"
                        >
                            {uploading ? <Loader2 className="w-6 h-6 animate-spin" /> : <Camera className="w-6 h-6" />}
                            {uploading ? 'Envoi en cours…' : photos.length > 0 ? 'Photo suivante' : 'Prendre une photo'}
                        </button>

                        <button
                            onClick={() => galleryRef.current?.click()}
                            disabled={uploading}
                            className="w-full max-w-xs border border-slate-600 text-slate-300 rounded-2xl py-3 flex items-center justify-center gap-2 text-sm font-medium"
                            data-testid="capture-gallery-btn"
                        >
                            <ImageIcon className="w-4 h-4" /> Choisir dans la galerie
                        </button>

                        {photos.length > 0 && (
                            <button
                                onClick={markDone}
                                disabled={uploading}
                                className="w-full max-w-xs bg-emerald-600 active:bg-emerald-700 disabled:opacity-60 rounded-2xl py-4 flex items-center justify-center gap-2 font-bold"
                                data-testid="capture-done-btn"
                            >
                                <Check className="w-5 h-5" /> Terminé — envoyer au PC ({photos.length})
                            </button>
                        )}

                        {multiple && photos.length === 0 && (
                            <p className="text-xs text-slate-500 text-center max-w-xs">
                                Vous pouvez enchaîner plusieurs photos (facture de plusieurs pages), puis appuyer sur « Terminé ».
                            </p>
                        )}
                    </>
                )}
            </main>
        </div>
    );
}
